import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  FileSpreadsheet,
  Download,
  Upload,
  Trash2,
  Edit,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Camera,
  FileText,
  Target,
  Loader2,
  RefreshCw,
  Eye,
  BookOpen,
  ExternalLink,
  Link2,
  CheckCheck
} from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Extracted AEP components
import { AEPStatsCards, AEPEmptyState } from '../components/aep';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AEP = () => {
  const [aeps, setAeps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAep, setSelectedAep] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // PGR Integration state
  const [syncingToPgr, setSyncingToPgr] = useState(null); // aep_id being synced
  const [syncStatus, setSyncStatus] = useState({}); // { aep_id: { synced, last_sync } }
  
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    setor: '',
    cargo: '',
    responsavel_avaliacao: '',
    data_avaliacao: new Date().toISOString().split('T')[0],
    checklist: [],
    plano_acao: [],
    observacoes: ''
  });
  
  const [newAction, setNewAction] = useState({
    acao: '',
    responsavel: '',
    prazo: '',
    status: 'pendente'
  });

  useEffect(() => {
    fetchAeps();
    fetchTemplate();
    
    // Listen for company changes
    const handleCompanyChange = () => {
      setLoading(true);
      fetchAeps();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    return () => window.removeEventListener('companyChanged', handleCompanyChange);
  }, []);

  const fetchAeps = async () => {
    try {
      const response = await axios.get(`${API_URL}/aep`);
      setAeps(response.data);
    } catch (error) {
      console.error('Error fetching AEPs:', error);
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/aep/template`);
      setTemplate(response.data);
      setFormData(prev => ({
        ...prev,
        checklist: response.data.checklist
      }));
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  // Fetch sync status for an AEP
  const fetchSyncStatus = async (aepId) => {
    try {
      const response = await axios.get(`${API_URL}/aep-integration/aep/${aepId}/sync-status`);
      setSyncStatus(prev => ({
        ...prev,
        [aepId]: response.data
      }));
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  // Sync AEP to PGR
  const handleSyncToPgr = async (aepId) => {
    setSyncingToPgr(aepId);
    try {
      const response = await axios.post(`${API_URL}/aep-integration/sync-to-pgr/${aepId}`);
      const { stats } = response.data;
      toast.success(
        `Sincronização concluída! Riscos: ${stats.risks_created} criados, ${stats.risks_updated} atualizados. ` +
        `Planos de ação: ${stats.action_plans_created} criados.`
      );
      // Refresh sync status
      fetchSyncStatus(aepId);
    } catch (error) {
      console.error('Error syncing to PGR:', error);
      if (error.response?.status === 400) {
        toast.warning(error.response?.data?.detail || 'Esta avaliação não possui itens inadequados para sincronizar');
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao sincronizar com PGR');
      }
    } finally {
      setSyncingToPgr(null);
    }
  };

  const handleChecklistChange = (categoryId, itemId, value) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.map(cat => {
        if (cat.id === categoryId) {
          return {
            ...cat,
            itens: cat.itens.map(item => {
              if (item.id === itemId) {
                return { ...item, resposta: value };
              }
              return item;
            })
          };
        }
        return cat;
      })
    }));
  };

  const handleSubmit = async () => {
    if (!formData.setor || !formData.cargo || !formData.responsavel_avaliacao) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/aep`, formData);
      toast.success('Avaliação AEP criada com sucesso!');
      setShowNewModal(false);
      resetForm();
      fetchAeps();
    } catch (error) {
      console.error('Error creating AEP:', error);
      toast.error('Erro ao criar avaliação');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    if (template) {
      setFormData({
        setor: '',
        cargo: '',
        responsavel_avaliacao: '',
        data_avaliacao: new Date().toISOString().split('T')[0],
        checklist: JSON.parse(JSON.stringify(template.checklist)),
        plano_acao: [],
        observacoes: ''
      });
    }
  };

  const handleViewAep = (aep) => {
    setSelectedAep(aep);
    setShowViewModal(true);
  };

  const handleDeleteAep = async (aepId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação?')) return;
    
    try {
      await axios.delete(`${API_URL}/aep/${aepId}`);
      toast.success('Avaliação excluída');
      fetchAeps();
    } catch (error) {
      toast.error('Erro ao excluir avaliação');
    }
  };

  const handleExportPdf = async (aepId) => {
    try {
      const response = await axios.get(`${API_URL}/aep/${aepId}/export-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `aep_${aepId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/aep/export-template/excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_aep.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template baixado!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!formData.setor || !formData.cargo || !formData.responsavel_avaliacao) {
      toast.error('Preencha setor, cargo e responsável antes de importar');
      return;
    }
    
    setImporting(true);
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('setor', formData.setor);
    formDataUpload.append('cargo', formData.cargo);
    formDataUpload.append('responsavel', formData.responsavel_avaliacao);
    
    try {
      const response = await axios.post(`${API_URL}/aep/import-excel`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      setShowNewModal(false);
      resetForm();
      fetchAeps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddAction = async () => {
    if (!selectedAep || !newAction.acao || !newAction.responsavel || !newAction.prazo) {
      toast.error('Preencha todos os campos da ação');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/aep/${selectedAep.id}/plano-acao`, {
        ...newAction,
        id: Date.now().toString()
      });
      
      toast.success('Ação adicionada!');
      setNewAction({ acao: '', responsavel: '', prazo: '', status: 'pendente' });
      
      // Refresh the AEP
      const response = await axios.get(`${API_URL}/aep/${selectedAep.id}`);
      setSelectedAep(response.data);
      fetchAeps();
    } catch (error) {
      toast.error('Erro ao adicionar ação');
    }
  };

  const handleUpdateActionStatus = async (aepId, acaoId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/aep/${aepId}/plano-acao/${acaoId}?status=${newStatus}`);
      toast.success('Status atualizado!');
      
      const response = await axios.get(`${API_URL}/aep/${aepId}`);
      setSelectedAep(response.data);
      fetchAeps();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  // Calculate radar chart data
  const getRadarData = (aep) => {
    if (!aep?.resultados?.por_categoria) return [];
    
    const categoryLabels = {
      'org_trabalho': 'Organização',
      'levantamento': 'Cargas',
      'mobiliario': 'Mobiliário',
      'maquinas': 'Máquinas',
      'conforto': 'Conforto'
    };
    
    return Object.entries(aep.resultados.por_categoria).map(([id, data]) => ({
      categoria: categoryLabels[id] || data.nome,
      inadequados: data.inadequados,
      total: data.total,
      fullMark: data.total
    }));
  };

  const getRiskBadge = (level) => {
    const config = {
      'Baixo': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'Moderado': { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      'Alto': { color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
      'Crítico': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const cfg = config[level] || config['Moderado'];
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {level}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      'pendente': 'bg-gray-100 text-gray-800',
      'em_andamento': 'bg-blue-100 text-blue-800',
      'concluido': 'bg-green-100 text-green-800'
    };
    const labels = {
      'pendente': 'Pendente',
      'em_andamento': 'Em Andamento',
      'concluido': 'Concluído'
    };
    return (
      <Badge className={config[status] || config['pendente']}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="aep-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Avaliação Ergonômica Preliminar (AEP)</h1>
          <p className="text-slate-600 mt-1">Conforme NR-17 - Ergonomia no Trabalho</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href="https://www.gov.br/trabalho-e-emprego/pt-br/assuntos/inspecao-do-trabalho/seguranca-e-saude-no-trabalho/canpat-2/canpat-2021/nova-nr17.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex"
            data-testid="manual-nr17-btn"
          >
            <Button variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800">
              <BookOpen className="h-4 w-4 mr-2" />
              Manual NR-17
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </a>
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-2" />
            Template Excel
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowNewModal(true); }}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="new-aep-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Avaliação
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <AEPStatsCards aeps={aeps} />

      {/* AEP List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-teal-600" />
            Avaliações Realizadas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {aeps.length === 0 ? (
            <AEPEmptyState onCreateClick={() => { resetForm(); setShowNewModal(true); }} />
          ) : (
            <div className="space-y-4">
              {aeps.map((aep) => (
                <div
                  key={aep.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`aep-item-${aep.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{aep.setor} - {aep.cargo}</h3>
                        {getRiskBadge(aep.resultados?.nivel_risco || 'N/A')}
                        {syncStatus[aep.id]?.synced && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            <CheckCheck className="h-3 w-3 mr-1" />
                            Sincronizado com PGR
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span>Responsável: {aep.responsavel_avaliacao}</span>
                        <span>Data: {new Date(aep.data_avaliacao).toLocaleDateString('pt-BR')}</span>
                        <span>Itens Inadequados: {aep.resultados?.total_inadequados || 0}/{aep.resultados?.total_itens || 0}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Sync to PGR Button */}
                      {aep.resultados?.total_inadequados > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSyncToPgr(aep.id)}
                          disabled={syncingToPgr === aep.id}
                          className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                          data-testid={`sync-pgr-${aep.id}`}
                          title="Sincronizar riscos ergonômicos com o PGR"
                        >
                          {syncingToPgr === aep.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Link2 className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleViewAep(aep)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleExportPdf(aep.id)}>
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteAep(aep.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Mini Radar Preview */}
                  {aep.resultados?.por_categoria && (
                    <div className="mt-4 h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={getRadarData(aep)}>
                          <PolarGrid stroke="#e5e7eb" />
                          <PolarAngleAxis dataKey="categoria" tick={{ fontSize: 10 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fontSize: 8 }} />
                          <Radar
                            name="Itens Inadequados"
                            dataKey="inadequados"
                            stroke="#ef4444"
                            fill="#ef4444"
                            fillOpacity={0.5}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New AEP Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-teal-600" />
              Nova Avaliação Ergonômica Preliminar
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="info">Informações</TabsTrigger>
              <TabsTrigger value="checklist">Checklist</TabsTrigger>
              <TabsTrigger value="import">Importar Excel</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Setor *</Label>
                  <Input
                    value={formData.setor}
                    onChange={(e) => setFormData({ ...formData, setor: e.target.value })}
                    placeholder="Ex: Produção, Administrativo"
                    data-testid="aep-setor-input"
                  />
                </div>
                <div>
                  <Label>Cargo *</Label>
                  <Input
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    placeholder="Ex: Operador, Auxiliar"
                    data-testid="aep-cargo-input"
                  />
                </div>
                <div>
                  <Label>Responsável pela Avaliação *</Label>
                  <Input
                    value={formData.responsavel_avaliacao}
                    onChange={(e) => setFormData({ ...formData, responsavel_avaliacao: e.target.value })}
                    placeholder="Nome do avaliador"
                    data-testid="aep-responsavel-input"
                  />
                </div>
                <div>
                  <Label>Data da Avaliação</Label>
                  <Input
                    type="date"
                    value={formData.data_avaliacao}
                    onChange={(e) => setFormData({ ...formData, data_avaliacao: e.target.value })}
                    data-testid="aep-data-input"
                  />
                </div>
              </div>
              <div>
                <Label>Observações</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  placeholder="Observações adicionais..."
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="checklist" className="space-y-6">
              {formData.checklist.map((categoria) => (
                <div key={categoria.id} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-4 text-teal-700">{categoria.nome}</h3>
                  <div className="space-y-3">
                    {categoria.itens.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          item.resposta === true
                            ? 'bg-red-50 border border-red-200'
                            : item.resposta === false
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <span className="flex-1 text-sm">{item.pergunta}</span>
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant={item.resposta === true ? 'default' : 'outline'}
                            className={item.resposta === true ? 'bg-red-600 hover:bg-red-700' : ''}
                            onClick={() => handleChecklistChange(categoria.id, item.id, true)}
                          >
                            Sim
                          </Button>
                          <Button
                            size="sm"
                            variant={item.resposta === false ? 'default' : 'outline'}
                            className={item.resposta === false ? 'bg-green-600 hover:bg-green-700' : ''}
                            onClick={() => handleChecklistChange(categoria.id, item.id, false)}
                          >
                            Não
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="import" className="space-y-4">
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600 mb-4">
                  Faça upload de um arquivo Excel com os dados do checklist
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Use o template disponível para garantir o formato correto
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls"
                  onChange={handleImportExcel}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing || !formData.setor || !formData.cargo || !formData.responsavel_avaliacao}
                >
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Selecionar Arquivo
                    </>
                  )}
                </Button>
                {(!formData.setor || !formData.cargo || !formData.responsavel_avaliacao) && (
                  <p className="text-sm text-amber-600 mt-2">
                    Preencha setor, cargo e responsável na aba "Informações" antes de importar
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={creating}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="save-aep-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Salvar Avaliação
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View AEP Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-teal-600" />
              {selectedAep?.setor} - {selectedAep?.cargo}
            </DialogTitle>
          </DialogHeader>

          {selectedAep && (
            <Tabs defaultValue="resultado" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="resultado">Resultado</TabsTrigger>
                <TabsTrigger value="checklist">Checklist</TabsTrigger>
                <TabsTrigger value="grafico">Gráfico</TabsTrigger>
                <TabsTrigger value="plano">Plano de Ação</TabsTrigger>
              </TabsList>

              <TabsContent value="resultado" className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-gray-900">
                        {selectedAep.resultados?.total_itens || 0}
                      </p>
                      <p className="text-sm text-gray-500">Total de Itens</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50">
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-red-600">
                        {selectedAep.resultados?.total_inadequados || 0}
                      </p>
                      <p className="text-sm text-red-700">Itens Inadequados</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      {getRiskBadge(selectedAep.resultados?.nivel_risco || 'N/A')}
                      <p className="text-sm text-gray-500 mt-2">Nível de Risco</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Results Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Resultado do Checklist</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3">Categoria</th>
                          <th className="text-center p-3">Total</th>
                          <th className="text-center p-3">Inadequados</th>
                          <th className="text-center p-3">Condição</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selectedAep.resultados?.por_categoria || {}).map(([id, data]) => (
                          <tr key={id} className="border-b">
                            <td className="p-3">{data.nome}</td>
                            <td className="text-center p-3">{data.total}</td>
                            <td className="text-center p-3">
                              <span className={data.inadequados > 0 ? 'text-red-600 font-semibold' : 'text-green-600'}>
                                {data.inadequados}
                              </span>
                            </td>
                            <td className="text-center p-3">
                              <Badge className={data.inadequados > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                                {data.inadequados > 0 ? 'INADEQUADA' : 'ADEQUADA'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="checklist" className="space-y-4">
                {selectedAep.checklist?.map((categoria) => (
                  <Card key={categoria.id}>
                    <CardHeader>
                      <CardTitle className="text-lg text-teal-700">{categoria.nome}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {categoria.itens?.map((item) => (
                          <div
                            key={item.id}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              item.resposta === true
                                ? 'bg-red-50 border border-red-200'
                                : item.resposta === false
                                ? 'bg-green-50 border border-green-200'
                                : 'bg-gray-50'
                            }`}
                          >
                            <span className="flex-1">{item.pergunta}</span>
                            <Badge className={
                              item.resposta === true
                                ? 'bg-red-600 text-white'
                                : item.resposta === false
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-400 text-white'
                            }>
                              {item.resposta === true ? 'SIM (Inadequado)' : item.resposta === false ? 'NÃO (Adequado)' : 'N/A'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="grafico">
                <Card>
                  <CardHeader>
                    <CardTitle>Gráfico de Radar - Criticidade por Categoria</CardTitle>
                    <CardDescription>
                      Visualização dos itens inadequados por categoria
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={getRadarData(selectedAep)}>
                          <PolarGrid stroke="#e5e7eb" />
                          <PolarAngleAxis dataKey="categoria" tick={{ fontSize: 12, fill: '#374151' }} />
                          <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fontSize: 10 }} />
                          <Radar
                            name="Itens Inadequados"
                            dataKey="inadequados"
                            stroke="#ef4444"
                            fill="#ef4444"
                            fillOpacity={0.5}
                          />
                          <Radar
                            name="Total de Itens"
                            dataKey="total"
                            stroke="#3b82f6"
                            fill="#3b82f6"
                            fillOpacity={0.1}
                          />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="plano" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Plano de Ação - AEP</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Add Action Form */}
                    <div className="grid grid-cols-5 gap-2 p-4 bg-gray-50 rounded-lg">
                      <Input
                        placeholder="Ação corretiva"
                        value={newAction.acao}
                        onChange={(e) => setNewAction({ ...newAction, acao: e.target.value })}
                      />
                      <Input
                        placeholder="Responsável"
                        value={newAction.responsavel}
                        onChange={(e) => setNewAction({ ...newAction, responsavel: e.target.value })}
                      />
                      <Input
                        type="date"
                        value={newAction.prazo}
                        onChange={(e) => setNewAction({ ...newAction, prazo: e.target.value })}
                      />
                      <Select
                        value={newAction.status}
                        onValueChange={(v) => setNewAction({ ...newAction, status: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="em_andamento">Em Andamento</SelectItem>
                          <SelectItem value="concluido">Concluído</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={handleAddAction} className="bg-teal-600 hover:bg-teal-700">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Actions Table */}
                    {selectedAep.plano_acao?.length > 0 ? (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3">Ação</th>
                            <th className="text-left p-3">Responsável</th>
                            <th className="text-center p-3">Prazo</th>
                            <th className="text-center p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAep.plano_acao.map((acao) => (
                            <tr key={acao.id} className="border-b">
                              <td className="p-3">{acao.acao}</td>
                              <td className="p-3">{acao.responsavel}</td>
                              <td className="text-center p-3">
                                {acao.prazo ? new Date(acao.prazo).toLocaleDateString('pt-BR') : '-'}
                              </td>
                              <td className="text-center p-3">
                                <Select
                                  value={acao.status}
                                  onValueChange={(v) => handleUpdateActionStatus(selectedAep.id, acao.id, v)}
                                >
                                  <SelectTrigger className="w-32">
                                    {getStatusBadge(acao.status)}
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pendente">Pendente</SelectItem>
                                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                                    <SelectItem value="concluido">Concluído</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma ação cadastrada</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleExportPdf(selectedAep?.id)}>
              <FileText className="h-4 w-4 mr-2" />
              Exportar PDF
            </Button>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AEP;
